import { Input, Output, EventEmitter, Component, OnDestroy, ViewEncapsulation } from '@angular/core';
import { ILanguage } from '../../types/language';
import { IMenu, ISidebarOptions, ISidebarChecklist, ISidebarChecklistLink } from '../../types/menu';
import { Observable, Subscription, isObservable } from 'rxjs';
import { CommonModule } from '@angular/common';
import { OpLanguageSelector } from '../opLanguageSelector/opLanguageSelector.component';
import { OpFilterMenuComponent } from '../opFilterMenu/opFilterMenu.component';
import { OpSidebarChecklist } from '../opSidebarChecklist/opSidebarChecklist.component';
import { OpMainMenu } from '../opMainMenu/opMainMenu.component';
import { OpPrimarySidebarService } from './opPrimarySidebar.service';
import { EmSidebar, EmSidebarMode, EmSidebarPosition } from '@everymundo/em-cms-kit/emSidebar';
import { OverlayscrollbarsModule } from 'overlayscrollbars-ngx';
import { PartialOptions } from "overlayscrollbars";

@Component({
    selector: 'op-primary-sidebar',
    templateUrl: "./opPrimarySidebar.view.html",
    styleUrls: [
        "../../../node_modules/overlayscrollbars/styles/overlayscrollbars.min.css"
    ],
    encapsulation: ViewEncapsulation.None,
    imports: [
        CommonModule,
        OpLanguageSelector,
        OpFilterMenuComponent,
        OpSidebarChecklist,
        OpMainMenu,
        EmSidebar,
        OverlayscrollbarsModule
    ]
})
export class OpPrimarySidebar implements OnDestroy {

  protected sections: Array<IMenu> = [];
  protected showLanguageToggle: boolean = true;
  protected showMenuFilter: boolean = true;

  protected showChecklist: boolean = false;
  protected checklistTitle: string = "";
  protected checklistLink: ISidebarChecklistLink = null;
  protected checklistDesc: ISidebarChecklist["description"] = "total";
  protected progress: number = 0;
  protected total: number = 10;
  protected menuAccordionMode: boolean = false;
  protected position = EmSidebarPosition.Left;
  protected mode = EmSidebarMode.Default;
  protected showDivider: boolean = false;
  protected menuFixedFirstLevel: boolean = false;

  private _checklistSubscription: Subscription;
  private _serviceSubscription = this.$service.change.subscribe((value) => this.menuOptions = value);

  protected readonly _overlayScrollOptions: PartialOptions = {
    overflow: {
      x: 'hidden'
    },
    update: {
      elementEvents: [['*', 'transitionstart transitionend']]
    },
    scrollbars: {
      autoHide: 'leave'
    }
  }

  @Input() set menuOptions(value: ISidebarOptions) {
    if (!value) return;
    this.sections = value.sections || [];
    this.showLanguageToggle = value.languageToggle != undefined ? value.languageToggle : true;
    this.showMenuFilter = value.menuFilter != undefined ? value.menuFilter : true;
    this.menuAccordionMode = value.accordionMode != undefined ? value.accordionMode : true;
    this._checklist = value.checklist;
    this.position = value.position != undefined ? value.position : this.position;
    this.mode = value.mode != undefined ? value.mode : this.mode;
    this.showDivider = this.showLanguageToggle || this.showMenuFilter || this.showChecklist;
    this.menuFixedFirstLevel = this.menuFixedFirstLevel != undefined ? value.fixedFirstLevel : false
  }

  @Input() languages: Array<ILanguage>;
  @Input() currentLanguage: ILanguage;
  @Output() onLanguageChange = new EventEmitter<ILanguage>();

  constructor(
    private $service: OpPrimarySidebarService
  ) {}

  private set _checklist(value: false | ISidebarChecklist | Observable<ISidebarChecklist>) {
    if (value == false || value == undefined || value == null) this.showChecklist = false;
    else if (isObservable(value)) {
      this._checklistSubscription = value.subscribe((value) => {
        this.showChecklist = !value.hidden;
        this.total = value.total;
        this.progress = value.progress;
        this.checklistTitle = value.title || "";
        this.checklistDesc = value.description || "total";
        this.checklistLink = value.link;
        this.showDivider = this.showLanguageToggle || this.showMenuFilter || this.showChecklist;
      })
    }
    else {
      this._checklistSubscription?.unsubscribe();
      this._checklistSubscription = null;
      this.showChecklist = !value.hidden;
      this.total = value.total;
      this.progress = value.progress;
      this.checklistTitle = value.title || "";
      this.checklistDesc = value.description || "total";
      this.checklistLink = value.link;
    }
  }

  ngOnDestroy(): void {
    if (this._checklistSubscription) this._checklistSubscription.unsubscribe();
    this._serviceSubscription.unsubscribe();
  }

  languageChange(language: ILanguage) {
    this.onLanguageChange.emit(language);
  }
}
